import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';
import {
  Avatar,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  IconButton,
  Popover,
  Stack,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Circle as CircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import moment from 'moment';

import {
  ISectionForAdmin,
  deleteCourseSectionByIdAsAdmin,
  getSectionsState,
} from '../../../../../store/V2/SectionSlice';
import { getAuthState } from '../../../../../store/AuthSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../store/UserSlice';
import { TP_COLORS } from '../../../../../constants';
import { useAppSelector } from '../../../../../hooks';
import {
  TClassJoinStatusData,
  checkUserPermission,
  getClassJoinStatusData,
} from '../../../../../helper';
import ViewZoomRecordingsModal from '../../modals/ViewZoomRecordingsModal';
import ClassJoinTimeStatusViewerPopperComponent from '../../components/ClassJoinTimeStatusViewerPopperComponent';

interface ZoomSectionItemProps {
  orderNumber: number;
  section: ISectionForAdmin;
}

const classLinkButtonStyles: SxProps = {
  backgroundColor: '#3498DB',
  color: TP_COLORS.white,
  p: '8px',
  borderRadius: '5px',
  fontSize: '12px',
  fontWeight: 600,
};

const containerStyles: SxProps = {
  backgroundColor: 'transparent',
  borderRadius: '10px',
  border: '1px dashed #CBD5E1',
};

const CustomCircleIcon = (
  <CircleIcon sx={{ width: '7px', height: '7px', color: '#CBD5E1' }} />
);

const ZoomSectionItem: React.FC<ZoomSectionItemProps> = ({
  orderNumber,
  section,
}) => {
  const confirm = useConfirm();
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const sectionsState = useAppSelector(getSectionsState);

  // State
  const [
    isViewZoomRecordingsModalOpen,
    setViewZoomRecordingsModalOpen,
  ] = useState(false);

  const [iconAnchorEl, setIconAnchorEl] = useState<HTMLElement | null>(null);

  let formatedDuration = '';
  let studentJoinData: TClassJoinStatusData | undefined;
  let tutorJoinData: TClassJoinStatusData | undefined;

  const popOverId = `${section._id}-mouse-over-popover`;
  const isPopOverOpen = Boolean(iconAnchorEl);
  const formatedDateTime = section.dateTime
    ? moment(section.dateTime).format('ddd MMM DD YYYY hh:mm A')
    : '';

  if (section.duration) {
    const _hours = Math.floor(section.duration / 60);
    const _mins = section.duration % 60;

    if (_hours) {
      formatedDuration = `${_hours} hr`;

      if (_mins) {
        formatedDuration += ` ${_mins} min`;
      }
    } else {
      formatedDuration += ` ${_mins} min`;
    }
  }

  if (section.dateTime) {
    studentJoinData = getClassJoinStatusData(
      section.dateTime,
      section.studentJoinTime
    );

    tutorJoinData = getClassJoinStatusData(
      section.dateTime,
      section.tutorJoinTime
    );
  }

  const handleDelete = () => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this section?',
    }).then(() => {
      dispatch(deleteCourseSectionByIdAsAdmin(section._id));
    });
  };

  const toggleViewZoomRecordingsModal = () => {
    setViewZoomRecordingsModalOpen((prevValue) => !prevValue);
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setIconAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setIconAnchorEl(null);
  };

  return (
    <>
      <Card elevation={0} sx={containerStyles}>
        <CardContent sx={{ p: '16px' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" flexGrow={1} mr="15px">
              <Avatar
                sx={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: '#1C9FF7',
                  mr: '15px',
                }}
              >
                {orderNumber}
              </Avatar>
              <Stack gap={1}>
                <Chip
                  size="small"
                  label="Zoom"
                  color="info"
                  sx={{ width: 'fit-content' }}
                />
                <Typography
                  title={section.title}
                  variant="subtitle2"
                  fontWeight={600}
                  className="ellipsis-text one-line"
                  maxWidth="calc(100% - 20px)"
                >
                  {section.title}
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                >
                  <Typography fontWeight={400} fontSize="14px" color="#1E293B">
                    {formatedDateTime}
                  </Typography>
                  {CustomCircleIcon}
                  <Typography fontWeight={400} fontSize="14px" color="#1E293B">
                    <b>Duration:</b> {formatedDuration}
                  </Typography>
                  {section.dateTime && moment(section.dateTime) < moment() ? (
                    <>
                      {CustomCircleIcon}
                      <Typography
                        fontSize="14px"
                        fontWeight={500}
                        color="info.main"
                        display="flex"
                        alignItems="center"
                        aria-owns={isPopOverOpen ? popOverId : undefined}
                        aria-haspopup="true"
                        onMouseEnter={handlePopoverOpen}
                        onMouseLeave={handlePopoverClose}
                      >
                        User&apos;s Joined Times
                        <>
                          &nbsp;
                          <InfoIcon
                            sx={{
                              color: '#000000',
                              width: '15px',
                              height: '15px',
                            }}
                          />
                        </>
                      </Typography>
                    </>
                  ) : null}
                  {section.zoomRecordings && section.zoomRecordings.length ? (
                    <>
                      {CustomCircleIcon}
                      <ButtonBase
                        onClick={toggleViewZoomRecordingsModal}
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#1E293B',
                          textDecoration: 'underline',
                        }}
                      >
                        Recordings
                      </ButtonBase>
                    </>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center">
              <Stack direction="row" alignItems="center" gap={1}>
                {section.studentShortUrl ? (
                  <ButtonBase
                    href={section.studentShortUrl}
                    target="_blank"
                    sx={classLinkButtonStyles}
                  >
                    Student Link
                  </ButtonBase>
                ) : null}
                {section.tutorShortUrl ? (
                  <ButtonBase
                    href={section.tutorShortUrl}
                    target="_blank"
                    sx={classLinkButtonStyles}
                  >
                    Tutor Link
                  </ButtonBase>
                ) : null}
              </Stack>
              {checkUserPermission(authState.user, {
                adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
              }) ? (
                <Tooltip title="Delete">
                  <IconButton
                    color="error"
                    onClick={handleDelete}
                    disabled={sectionsState.status === 'loading'}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              {/* <Tooltip title="Delete">
              <IconButton
                onClick={() => onEdit(section)}
                disabled={sectionsState.status === 'loading'}
              >
                <EditIcon />
              </IconButton>
            </Tooltip> */}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      {/* MODALS */}
      {isViewZoomRecordingsModalOpen ? (
        <ViewZoomRecordingsModal
          open={isViewZoomRecordingsModalOpen}
          onClose={toggleViewZoomRecordingsModal}
          zoomRecordings={section.zoomRecordings || []}
        />
      ) : null}
      <Popover
        disableRestoreFocus
        id={popOverId}
        open={isPopOverOpen}
        anchorEl={iconAnchorEl}
        onClose={handlePopoverClose}
        PaperProps={{
          elevation: 0,
          sx: {
            p: '15px',
            borderRadius: '20px',
            border: '1px solid #E2E8F0',
            boxShadow: '0px 14px 40px 0px #6E9DB22E',
            maxWidth: '260px',
          },
        }}
        sx={{
          pointerEvents: 'none',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <ClassJoinTimeStatusViewerPopperComponent
          tutorJoinData={tutorJoinData}
          studentJoinData={studentJoinData}
          tutorJoinTime={section.tutorJoinTime}
          studentJoinTime={section.studentJoinTime}
        />
      </Popover>
    </>
  );
};

export default ZoomSectionItem;
